/**
 * Manage Repositories page — Greptile's "Add/Remove Repos" table.
 * Server-side pagination + search via `?page=&search=`; per-row
 * enable/disable toggle persisted via PATCH. Optimistic flip on
 * toggle, rollback on PATCH error.
 *
 * `installation_id` comes from the URL query string; the api Worker's
 * `resolveInstallationId` falls back to `FELUDA_DEFAULT_INSTALLATION_ID`
 * when the param is missing.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyRepositoriesHero } from '../components/EmptyRepositoriesHero.js';
import { EmptyState } from '../components/EmptyState.js';
import { Pagination } from '../components/Pagination.js';
import { SearchInput } from '../components/SearchInput.js';
import { Toggle } from '../components/Toggle.js';
import {
  useRepositories,
  useUpdateRepository,
  type InstallationRepository,
} from '../api.js';

const DEFAULT_PAGE_SIZE = 10;

export function RepositoriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const installationId = Number(searchParams.get('installation_id') ?? 0);
  const page = Math.max(1, Number(searchParams.get('page') ?? 1));
  const search = searchParams.get('search') ?? '';

  // Local draft state — when the user types, we update the URL on a
  // 300 ms debounce (SearchInput's responsibility). Page resets to 1
  // on every search-string change.
  const [searchDraft, setSearchDraft] = useState(search);

  const query = useRepositories(installationId, page, search);
  const mutation = useUpdateRepository(installationId);

  const onSearchChange = (next: string) => {
    setSearchDraft(next);
    const params = new URLSearchParams(searchParams);
    if (next) params.set('search', next);
    else params.delete('search');
    params.set('page', '1');
    setSearchParams(params, { replace: true });
  };

  const onPageChange = (next: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(next));
    setSearchParams(params, { replace: true });
  };

  const onToggle = (row: InstallationRepository) => {
    mutation.mutate({ repoId: row.repoId, enabled: !row.enabled });
  };

  if (!installationId) {
    return (
      <EmptyState
        title="Missing installation"
        body="Append ?installation_id=<id> to the URL to load a list of repositories."
      />
    );
  }

  if (query.isLoading) {
    return (
      <div className="feluda-settings">
        <header className="feluda-settings-header">
          <h1 className="feluda-settings-title">Manage Repositories</h1>
        </header>
        <p className="feluda-settings-subtitle">Loading…</p>
      </div>
    );
  }

  if (query.isError) {
    return (
      <EmptyState
        title="Failed to load repositories"
        body={query.error.message}
      />
    );
  }

  const result = query.data;
  if (!result) {
    return null;
  }

  if (result.total === 0) {
    return (
      <div className="feluda-settings">
        <header className="feluda-settings-header">
          <h1 className="feluda-settings-title">Manage Repositories</h1>
          <span className="feluda-settings-subtitle">
            Installation #{installationId}
          </span>
        </header>
        <EmptyRepositoriesHero
          title="No repositories yet"
          body="Grant the Feluda GitHub App access to a repo to see it here."
        />
      </div>
    );
  }

  return (
    <div className="feluda-settings">
      <header className="feluda-settings-header">
        <h1 className="feluda-settings-title">Manage Repositories</h1>
        <span className="feluda-settings-subtitle">
          {result.total} repos in installation #{installationId}
        </span>
      </header>

      <section className="feluda-section">
        <div className="feluda-section-header feluda-repositories-toolbar">
          <div className="feluda-section-header-text">
            <h2 className="feluda-section-title">Repositories</h2>
            <p className="feluda-section-description">
              Toggle which repositories Feluda should review.
            </p>
          </div>
          <SearchInput
            value={searchDraft}
            onSearch={onSearchChange}
            placeholder="Search repositories"
          />
        </div>

        <table className="feluda-table">
          <thead>
            <tr>
              <th className="feluda-table-col-repo">REPO</th>
              <th className="feluda-table-col-branch">DEFAULT BRANCH</th>
              <th className="feluda-table-col-visibility">VISIBILITY</th>
              <th className="feluda-table-col-status">STATUS</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row) => (
              <tr key={row.repoId} className="feluda-table-row">
                <td className="feluda-table-col-repo">
                  <span className="feluda-table-repo-icon" aria-hidden="true">
                    📦
                  </span>
                  <span className="feluda-table-repo-name">{row.repoId}</span>
                </td>
                <td className="feluda-table-col-branch">
                  <code className="feluda-table-branch">{row.defaultBranch}</code>
                </td>
                <td className="feluda-table-col-visibility">
                  {row.private ? 'Private' : 'Public'}
                </td>
                <td className="feluda-table-col-status">
                  <Toggle
                    checked={row.enabled}
                    onChange={() => onToggle(row)}
                    label={row.enabled ? 'Disable review' : 'Enable review'}
                  />
                  <span
                    className={`feluda-table-status-text${
                      row.enabled ? ' feluda-table-status-on' : ''
                    }`}
                  >
                    {row.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <Pagination
          page={page}
          pageSize={result.pageSize || DEFAULT_PAGE_SIZE}
          total={result.total}
          onPageChange={onPageChange}
        />
      </section>
    </div>
  );
}
